import {
  CaretDownFilled,
  CaretUpFilled,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import React, { ReactNode, useEffect, useState } from 'react';
import styles from './Section.module.css';

type Props = {
  name: string;
  icon?: ReactNode;
  children: JSX.Element | JSX.Element[];
  className?: string;
  hasActiveFilters?: boolean;
  activePreset?: string;
  hide?: boolean;
  expandOnMount?: boolean;
  arrowStyle?: 'caret' | 'outlined';
  css?: {
    title?: string;
    heading?: string;
    expandedConatiner?: string;
  };
  setScrollToId?: (id: string) => void;
};

export const Section = ({
  name,
  icon,
  children,
  className,
  hasActiveFilters,
  hide,
  expandOnMount,
  css,
  arrowStyle,
  activePreset,
  setScrollToId,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(expandOnMount || false);

  const hasCheckedChildCheckboxes =
    Array.isArray(children) &&
    (children.some(child => child?.props?.isChecked) ||
      !!children
        ?.map(c => c?.props)
        .filter(
          secondLevelChild =>
            secondLevelChild?.children &&
            Array.isArray(secondLevelChild.children) &&
            secondLevelChild.children?.some(
              thirdLevelChild => thirdLevelChild?.props?.isChecked,
            ),
        ).length);

  const activeFiltersPresent = hasCheckedChildCheckboxes || hasActiveFilters;

  useEffect(() => {
    if (activePreset && activeFiltersPresent) {
      setIsExpanded(true);
      setScrollToId?.(name);
    }
  }, [activePreset, activeFiltersPresent]);

  if (hide) return null;

  return (
    <div
      id={name}
      className={classNames(
        styles.container,
        className,
        isExpanded && css?.expandedConatiner,
        {
          [styles.highlighted]: activePreset && activeFiltersPresent,
        },
      )}
    >
      <div
        className={classNames(styles.title, css?.title, {
          [styles.titleExpanded]: isExpanded,
        })}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className={classNames(styles.heading, css?.heading)}>
          {icon}
          {name}
          {activeFiltersPresent && <div className={styles.activeDot} />}
        </div>
        {isExpanded ? (
          arrowStyle === 'outlined' ? (
            <UpOutlined className={styles.outlined} />
          ) : (
            <CaretUpFilled className={styles.caret} />
          )
        ) : arrowStyle === 'outlined' ? (
          <DownOutlined className={styles.outlined} />
        ) : (
          <CaretDownFilled className={styles.caret} />
        )}
      </div>
      {isExpanded && children}
    </div>
  );
};
